import React from "react";
import { Globe, Radio, Workflow, Cpu, Building2, ArrowRight } from "lucide-react";

/**
 * Inside Connexis — Four Modes Diagram
 *
 * A clean light-themed orchestral diagram showing all four modes converging
 * on the Connexis routing engine, then flowing out to buyers + AI systems.
 *
 * Pure CSS + SVG. No 3D. Matches site design tokens.
 */
export default function InsidePlatformDiagram() {
  return (
    <div className="relative rounded-3xl border border-slate-200 bg-white shadow-soft p-8 sm:p-12 overflow-hidden" data-testid="inside-platform-diagram">
      <div className="absolute inset-0 dot-grid dot-grid-fade opacity-50" />
      <div className="relative">
        {/* Heading strip */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-10">
          <div className="flex items-center gap-2.5">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">Connexis routing engine · v2.4</span>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-600">
              <span className="relative flex items-center justify-center size-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full size-1.5 bg-emerald-500" />
              </span>
              Live · 98.6ms median
            </span>
          </div>
          <span className="text-[10.5px] font-mono tabular-nums text-slate-500">240k+ enquiries / mo</span>
        </div>

        {/* Three-column flow: Sources → Engine → Outputs */}
        <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-6 items-stretch">
          {/* LEFT — Sources */}
          <div className="space-y-3">
            <SourceNode
              Icon={Globe}
              tone="navy"
              label="First-party brands"
              sub="Assura · QuickFinance · SwiftCash · Solve Debts"
            />
            <SourceNode
              Icon={Radio}
              tone="blue"
              label="Partner publishers"
              sub="API ingest · hosted landers · 90% revshare"
            />
            <SourceNode
              Icon={Cpu}
              tone="slate"
              label="AI operational input"
              sub="Voice qualification · chat replies · WhatsApp"
            />
          </div>

          {/* CENTER — Engine */}
          <div className="flex items-center justify-center lg:px-2">
            <div className="relative w-full lg:w-[220px]">
              {/* Animated flow line — desktop only */}
              <div className="hidden lg:block absolute inset-y-0 -left-6 w-6 overflow-hidden pointer-events-none">
                <FlowLine />
              </div>
              <div className="hidden lg:block absolute inset-y-0 -right-6 w-6 overflow-hidden pointer-events-none">
                <FlowLine reverse />
              </div>

              <div className="rounded-2xl border-2 border-[#0055FF]/30 bg-gradient-to-b from-[#0055FF]/[0.06] to-white p-6 text-center shadow-[0_0_0_4px_rgba(0,85,255,0.04)]">
                <span className="inline-grid place-items-center size-12 rounded-xl bg-[#0F172A] text-white">
                  <Workflow className="size-6" strokeWidth={1.5} />
                </span>
                <div className="mt-4 text-[13px] font-semibold text-slate-900 tracking-tight">Connexis routing engine</div>
                <div className="mt-1 text-[11px] text-slate-500 leading-snug">Dedup · score · enrich · route</div>
                <div className="mt-4 grid grid-cols-3 gap-1.5">
                  {["Score", "Match", "Post"].map((t, i) => (
                    <div key={i} className="text-[9.5px] font-mono tabular-nums text-slate-500 rounded bg-slate-50 border border-slate-200 py-1">
                      {t}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Destinations */}
          <div className="space-y-3">
            <SourceNode
              Icon={Building2}
              tone="navy"
              label="Exclusive buyer routing"
              sub="Wallet debit · webhook deliver · AI follow-up"
              align="right"
            />
            <SourceNode
              Icon={Workflow}
              tone="blue"
              label="Postback + retry queue"
              sub="HMAC-signed · 5-attempt backoff · audit log"
              align="right"
            />
            <SourceNode
              Icon={ArrowRight}
              tone="slate"
              label="Replies + AI triage"
              sub="Auto-classified · suggested · auto-action"
              align="right"
            />
          </div>
        </div>

        {/* Bottom legend strip */}
        <div className="mt-10 pt-6 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3 text-[11.5px] text-slate-500">
          <div className="flex items-center gap-4 flex-wrap">
            <LegendDot color="#0F172A" label="First-party (we own)" />
            <LegendDot color="#0055FF" label="Partner network" />
            <LegendDot color="#94a3b8" label="AI surface" />
          </div>
          <span className="font-mono text-[10.5px] text-slate-400">Routing v2.4 · sub-second SLA</span>
        </div>
      </div>
    </div>
  );
}

function SourceNode({ Icon, tone, label, sub, align = "left" }) {
  const ring = tone === "navy" ? "border-[#0F172A]/30" : tone === "blue" ? "border-[#0055FF]/30" : "border-slate-300";
  const bg = tone === "navy" ? "bg-[#0F172A]" : tone === "blue" ? "bg-[#0055FF]" : "bg-slate-500";
  return (
    <div className={`rounded-xl border-2 ${ring} bg-white p-4 flex items-center gap-4 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
      <span className={`inline-grid place-items-center size-10 rounded-lg ${bg} text-white shrink-0`}>
        <Icon className="size-5" strokeWidth={1.6} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-slate-900 tracking-tight">{label}</div>
        <div className="mt-0.5 text-[11.5px] text-slate-500 leading-snug truncate">{sub}</div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="size-2 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}

function FlowLine({ reverse }) {
  return (
    <svg width="24" height="100%" viewBox="0 0 24 200" preserveAspectRatio="none" className="absolute inset-0">
      <defs>
        <linearGradient id={`grad-${reverse ? "r" : "l"}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#0055FF" stopOpacity="0" />
          <stop offset="50%" stopColor="#0055FF" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#0055FF" stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="12" y1="0" x2="12" y2="200" stroke="#e2e8f0" strokeWidth="1" />
      <line x1="12" y1="0" x2="12" y2="60" stroke={`url(#grad-${reverse ? "r" : "l"})`} strokeWidth="2.5" strokeLinecap="round">
        <animate attributeName="y1" from={reverse ? "-60" : "0"} to={reverse ? "200" : "260"} dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="y2" from={reverse ? "0" : "60"} to={reverse ? "260" : "320"} dur="2.4s" repeatCount="indefinite" />
      </line>
    </svg>
  );
}
